import {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useUserContext } from '~/core/context'
import { DesignSystem } from '~/core/designSystem'
import { useApp } from './hooks/useApp'
import { useInstallation } from './hooks/useInstallation'
import { useSupported } from './hooks/useSupported'

const useInternal = () => {
  const [isPromotionPopupVisible, setPromotionPopupVisible] = useState(false)

  const { isSupported, isNotificationSupported, publicKey } = useSupported()
  const { isRunning } = useApp()
  const { session } = useUserContext()

  const installation = useInstallation()

  const designSystem = DesignSystem.use()

  useEffect(() => {
    if (
      session.isLoggedIn &&
      isSupported &&
      designSystem.isMobile &&
      designSystem.isTouchDevice &&
      !isRunning
    ) {
      installation.setVisible(isRunning === false)
    } else {
      installation.setVisible(false)
    }
  }, [isSupported, isRunning, designSystem.isMobile, session.isLoggedIn])

  return {
    publicKey,
    installation,
    isRunning,
    isSupported,
    isNotificationSupported,
    promotion: {
      isVisible: isPromotionPopupVisible,
      setVisible: setPromotionPopupVisible,
    },
  }
}

type PwaContext = ReturnType<typeof useInternal>

const PwaContext = createContext<PwaContext>(undefined)

export const PwaProvider = ({ children }: { children: ReactNode }) => {
  const value = useInternal()

  return <PwaContext.Provider value={value}>{children}</PwaContext.Provider>
}

export const usePwaContext = (): PwaContext => {
  const context = useContext(PwaContext)

  if (context === undefined) {
    throw new Error('usePwaContext must be used within a PwaProvider')
  }

  return context
}
